import { PosConfigurationItem } from './_types';

export interface CollectionTags {
  productTags?: string[];
  packageTags?: string[];
}

type CollectionItemType = 'product' | 'package';

// Legacy index ids: product-tag-0, package-tag-1 (index-based).
const LEGACY_INDEXED_TAG_ID_PATTERN = /^(product|package)-tag-\d+$/;

// Current format: product-tag-Apparel, package-tag-Membership (type + tag name).
const PREFIXED_NAME_TAG_ID_PATTERN = /^(product|package)-tag-(.+)$/;

/** Build a namespaced POS collection itemId/filterParams ref for a product/package tag. */
export function formatCollectionTagId(
  itemType: CollectionItemType,
  tagName: string
): string {
  return `${itemType}-tag-${tagName}`;
}

function isIndexedTagId(value: string): boolean {
  return LEGACY_INDEXED_TAG_ID_PATTERN.test(value);
}

function matchPlatformTagName(
  tagList: string[] | undefined,
  candidate?: string
): string | undefined {
  if (!candidate) {
    return undefined;
  }

  return (
    tagList?.find((tag) => tag.toLowerCase() === candidate.toLowerCase()) ??
    candidate
  );
}

/**
 * This grabs the tag name from the string passed in. The value passed in can be
 * the item id or, as a backup, the filter params.
 *
 * For legacy indexed ids, it returns undefined as we can't extract the name
 * from them.
 *
 * For the new, titled ids, it strips the "product-tag-" and "package-tag-"
 * prefixes if they are there and returns just the tag name.
 */
function buildTagName(value?: string): string | undefined {
  if (!value || isIndexedTagId(value)) {
    return undefined;
  }

  return value.match(PREFIXED_NAME_TAG_ID_PATTERN)?.[2] ?? value;
}

/**
 * Resolves the current tag name for a POS collection tile.
 *
 * Product/package tags are stored on the platform as string arrays — they do
 * not have stable database IDs (unlike workout-plan tags in excom_tags). POS
 * tiles reference tags via `product-tag-{name}` / `package-tag-{name}` itemIds.
 *
 * Legacy configs used index-based IDs (product-tag-N). For those, the stored
 * `name` field is the only reliable identifier.
 */
export function resolveCollectionTagName(
  item: Pick<
    PosConfigurationItem,
    'name' | 'type' | 'itemId' | 'filterParams' | 'itemType'
  >,
  tags?: CollectionTags
): string {
  if (item.type !== 'collection') {
    return item.name;
  }

  const tagList =
    item.itemType === 'package' ? tags?.packageTags : tags?.productTags;

  return (
    matchPlatformTagName(tagList, buildTagName(item.itemId)) ??
    matchPlatformTagName(tagList, buildTagName(item.filterParams)) ??
    matchPlatformTagName(tagList, item.name) ??
    item.name
  );
}
